import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { AdminKeyService } from '../../services/adminKeyService';

type PlatformSubscriber = {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  phone?: string;
  city?: string;
  state?: string;
  country?: string;
  zip?: string;
  groups?: string[];
  notes?: string;
  tags?: string[];
  createdAt?: string;
  updatedAt?: string;
};

const PlatformSubscribersTab: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [items, setItems] = React.useState<PlatformSubscriber[]>([]);
  const [updatedAt, setUpdatedAt] = React.useState<string | null>(null);

  const [q, setQ] = React.useState('');

  const [editOpen, setEditOpen] = React.useState(false);
  const [editEmail, setEditEmail] = React.useState('');
  const [editFirstName, setEditFirstName] = React.useState('');
  const [editLastName, setEditLastName] = React.useState('');
  const [editCompany, setEditCompany] = React.useState('');
  const [editPhone, setEditPhone] = React.useState('');
  const [editCity, setEditCity] = React.useState('');
  const [editState, setEditState] = React.useState('');
  const [editCountry, setEditCountry] = React.useState('');
  const [editZip, setEditZip] = React.useState('');
  const [editGroups, setEditGroups] = React.useState('');
  const [editTags, setEditTags] = React.useState('');
  const [editNotes, setEditNotes] = React.useState('');

  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteEmail, setDeleteEmail] = React.useState('');

  const callApi = React.useCallback(async (action: string, payload?: any) => {
    const token = await AdminKeyService.getSecret();
    if (!token) throw new Error('Missing admin token');

    const res = await fetch('/api/platform-subscribers.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ action, ...(payload || {}) }),
    });
    const raw = await res.text();
    let json: any = null;
    try {
      json = raw ? JSON.parse(raw) : null;
    } catch {
      json = null;
    }

    if (!res.ok || !json?.ok) {
      const msg = json?.error || raw || `${res.status} ${res.statusText}`;
      throw new Error(msg);
    }
    return json;
  }, []);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      const json = await callApi('list');
      setItems(Array.isArray(json?.items) ? json.items : []);
      setUpdatedAt(typeof json?.updatedAt === 'string' ? json.updatedAt : null);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  }, [callApi]);

  React.useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditEmail('');
    setEditFirstName('');
    setEditLastName('');
    setEditCompany('');
    setEditPhone('');
    setEditCity('');
    setEditState('');
    setEditCountry('');
    setEditZip('');
    setEditGroups('');
    setEditTags('');
    setEditNotes('');
    setEditOpen(true);
  };

  const openEdit = (s: PlatformSubscriber) => {
    setEditEmail(s.email || '');
    setEditFirstName(s.firstName || '');
    setEditLastName(s.lastName || '');
    setEditCompany(s.company || '');
    setEditPhone(s.phone || '');
    setEditCity(s.city || '');
    setEditState(s.state || '');
    setEditCountry(s.country || '');
    setEditZip(s.zip || '');
    setEditGroups(Array.isArray(s.groups) ? s.groups.join(', ') : '');
    setEditTags(Array.isArray(s.tags) ? s.tags.join(', ') : '');
    setEditNotes(s.notes || '');
    setEditOpen(true);
  };

  const save = async () => {
    const email = editEmail.trim();
    if (!email) {
      toast.error('Email is required');
      return;
    }

    const groups = editGroups
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const tags = editTags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    try {
      setSaving(true);
      await callApi('upsert', {
        subscriber: {
          email,
          firstName: editFirstName.trim() || undefined,
          lastName: editLastName.trim() || undefined,
          company: editCompany.trim() || undefined,
          phone: editPhone.trim() || undefined,
          city: editCity.trim() || undefined,
          state: editState.trim() || undefined,
          country: editCountry.trim() || undefined,
          zip: editZip.trim() || undefined,
          groups,
          notes: editNotes.trim() || undefined,
          tags,
        },
      });
      toast.success('Saved');
      setEditOpen(false);
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (email: string) => {
    setDeleteEmail(email);
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setSaving(true);
      await callApi('delete', { email: deleteEmail });
      toast.success('Deleted');
      setDeleteOpen(false);
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  const filtered = React.useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return items;
    return items.filter((s) => {
      const hay = [
        s.email,
        s.firstName,
        s.lastName,
        s.company,
        s.phone,
        s.city,
        s.state,
        s.country,
        s.zip,
        Array.isArray(s.groups) ? s.groups.join(' ') : '',
        Array.isArray(s.tags) ? s.tags.join(' ') : '',
        s.notes,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(query);
    });
  }, [items, q]);

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" fontWeight={900} gutterBottom>
              Platform Subscribers
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Saved locally in <code>storage/platform-subscribers.json</code>. Great for internal lists, notifications and quick targeting.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search email, name, tags..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              onClick={load}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            >
              Refresh
            </Button>
            <Button variant="contained" onClick={openCreate} startIcon={<AddIcon />}>
              Add
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            {updatedAt ? `Last updated: ${updatedAt}` : 'Not saved yet'}
          </Typography>
          <Chip
            label={`${items.length} total`}
            variant="outlined"
            sx={{ fontWeight: 800 }}
          />
        </Box>

        <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Groups</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((s) => (
                <TableRow key={s.email} hover>
                  <TableCell sx={{ fontWeight: 900 }}>{s.email}</TableCell>
                  <TableCell>{`${s.firstName || ''} ${s.lastName || ''}`.trim() || '-'}</TableCell>
                  <TableCell>{s.company || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(s.groups || []).slice(0, 3).map((t) => (
                        <Chip key={t} size="small" label={t} variant="outlined" />
                      ))}
                      {(s.groups || []).length > 3 && <Chip size="small" label={`+${(s.groups || []).length - 3}`} variant="outlined" />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(s.tags || []).slice(0, 4).map((t) => (
                        <Chip key={t} size="small" label={t} />
                      ))}
                      {(s.tags || []).length > 4 && <Chip size="small" label={`+${(s.tags || []).length - 4}`} />}
                    </Box>
                  </TableCell>
                  <TableCell>{s.updatedAt || '-'}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(s)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => confirmDelete(s.email)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}

              {filtered.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      No subscribers yet. Click “Add” to create your first platform subscriber.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editEmail ? 'Edit subscriber' : 'Add subscriber'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              fullWidth
              type="email"
              sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}
            />
            <TextField label="First name" value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} fullWidth />
            <TextField label="Last name" value={editLastName} onChange={(e) => setEditLastName(e.target.value)} fullWidth />
            <TextField label="Company" value={editCompany} onChange={(e) => setEditCompany(e.target.value)} fullWidth />
            <TextField label="Phone" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} fullWidth />
            <TextField label="City" value={editCity} onChange={(e) => setEditCity(e.target.value)} fullWidth />
            <TextField label="State" value={editState} onChange={(e) => setEditState(e.target.value)} fullWidth />
            <TextField label="Country" value={editCountry} onChange={(e) => setEditCountry(e.target.value)} fullWidth />
            <TextField label="ZIP" value={editZip} onChange={(e) => setEditZip(e.target.value)} fullWidth />
            <TextField
              label="Groups"
              value={editGroups}
              onChange={(e) => setEditGroups(e.target.value)}
              fullWidth
              helperText="Comma-separated"
              sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}
            />
            <TextField
              label="Tags"
              value={editTags}
              onChange={(e) => setEditTags(e.target.value)}
              fullWidth
              helperText="Comma-separated"
              sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}
            />
            <TextField
              label="Notes"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : undefined}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete subscriber?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will remove <b>{deleteEmail}</b> from <code>platform-subscribers.json</code>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={doDelete} color="error" variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : undefined}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlatformSubscribersTab;
